// safety/incidents-and-injuries/utils/incidents-utils.ts
import type { Incident, IncidentStatus, WorkflowStep, IncidentInvestigation, CorrectiveActionStatus } from '../../../../types/incident.types';

export const DETAIL_TABS = [
    'Overview',
    'RTW Plan',
    'Certificates',
    'Medical Appointments',
    'Medical Expenses',
    'Wage Reimbursements',
    'File Notes',
    'Incident Investigation',
    'Attachments',
    'Letter Templates',
] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

export const todayISO = () => new Date().toISOString().slice(0, 10);

// Allowed status transitions — mirrors the map enforced by the backend
// (SafetyIncidentService.updateStatus). Closed incidents can only be reopened.
export const STATUS_TRANSITIONS: Record<IncidentStatus, IncidentStatus[]> = {
    Open: ['In Progress', 'Under Investigation', 'Closed'],
    'In Progress': ['Open', 'Under Investigation', 'Closed'],
    'Under Investigation': ['Open', 'In Progress', 'Closed'],
    Closed: ['Open'],
};

/* ---------- RTW workflow ---------- */

// Step numbers of the canonical workflow (referenced by auto-triggers).
export const WORKFLOW_STEPS = {
    REPORT_COMPLETED: 1,
    EMPLOYER_NOTIFIED: 2,
    INSURER_NOTIFIED: 3,
    RTW_PREPARED: 4,
    RTW_APPROVED: 5,
    RETURNED_TO_WORK: 6,
    CLOSED: 7,
} as const;

// Canonical return-to-work workflow. The incident stores completion records
// sparsely (only steps acted upon); the UI merges stored state against this
// template, so older incidents show the full flow without a data migration.
// `auto` steps complete via triggers (creation, all approvals, close) — no button.
export const WORKFLOW_TEMPLATE: { step: number; label: string; auto: boolean }[] = [
    { step: WORKFLOW_STEPS.REPORT_COMPLETED, label: 'Injury Report Completed', auto: true },
    { step: WORKFLOW_STEPS.EMPLOYER_NOTIFIED, label: 'Employer / Supervisor Notified', auto: false },
    { step: WORKFLOW_STEPS.INSURER_NOTIFIED, label: 'Insurer Notified (Claim Lodged)', auto: false },
    { step: WORKFLOW_STEPS.RTW_PREPARED, label: 'RTW Plan Prepared', auto: false },
    { step: WORKFLOW_STEPS.RTW_APPROVED, label: 'RTW Plan Approved', auto: true },
    { step: WORKFLOW_STEPS.RETURNED_TO_WORK, label: 'Worker Returned to Work', auto: false },
    { step: WORKFLOW_STEPS.CLOSED, label: 'Incident Closed', auto: true },
];

// Merge the incident's sparse stored workflow against the template:
// every template step is present; stored records supply done/date/who.
export function mergedWorkflow(i: Incident): WorkflowStep[] {
    const stored = Array.isArray(i.workflow) ? i.workflow : [];
    return WORKFLOW_TEMPLATE.map((t) => {
        const s = stored.find((x) => Number(x.step) === t.step);
        return s ? { ...s, label: t.label, auto: t.auto } : { step: t.step, label: t.label, who: '', done: false, auto: t.auto };
    });
}

export const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 9)}`;

export function fmtDate(d?: string): string {
    if (!d) return '–';
    // Handles both 'YYYY-MM-DD' (date columns) and full ISO timestamps (createdAt/updatedAt)
    const dt = d.includes('T') ? new Date(d) : new Date(`${d}T00:00:00`);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const money = (n: number | string) => `$${Number(n || 0).toFixed(2)}`;

export const initials = (name = '') =>
    name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

export function daysUntil(d?: string): number {
    if (!d) return 999;
    return Math.round(
        (new Date(`${d}T00:00:00`).getTime() - new Date(`${todayISO()}T00:00:00`).getTime()) / 86400000,
    );
}

// Status pill classes (semantic tokens where possible; amber/emerald for the
// domain states, matching the screenshots).
export function statusPillClass(s: IncidentStatus): string {
    switch (s) {
        case 'Open':
            return 'text-destructive border border-destructive/30 bg-destructive/5';
        case 'In Progress':
            return 'text-amber-600 border border-amber-300 bg-amber-50';
        case 'Under Investigation':
            return 'text-primary border border-primary/30 bg-primary/5';
        case 'Closed':
            return 'text-emerald-600 border border-emerald-300 bg-emerald-50';
        default:
            return 'text-muted-foreground border border-border';
    }
}

export function capacityColor(c?: string): string {
    if (c === 'Full Capacity') return 'text-emerald-600';
    if (c === 'Light Duties') return 'text-amber-600';
    if ((c || '').startsWith('No Work')) return 'text-destructive';
    return 'text-muted-foreground';
}

/* ---------- Incident Investigation ---------- */

// Default investigation document — incidents created before this tab existed
// have no stored document, so the UI merges against these defaults (same
// approach as mergedWorkflow).
export const DEFAULT_INVESTIGATION: IncidentInvestigation = {
    notes: [],
    breachedProcedures: '',
    riskAssessmentReviewed: null,
    correctiveActions: [],
};

export function mergedInvestigation(i: Incident): IncidentInvestigation {
    const inv = i.investigation;
    return {
        notes: inv && Array.isArray(inv.notes) ? inv.notes : [],
        breachedProcedures: inv?.breachedProcedures ?? '',
        riskAssessmentReviewed: inv?.riskAssessmentReviewed ?? null,
        correctiveActions: inv && Array.isArray(inv.correctiveActions) ? inv.correctiveActions : [],
    };
}

export function correctiveActionStatusClass(s: CorrectiveActionStatus): string {
    switch (s) {
        case 'Open':
            return 'text-amber-600 border border-amber-300 bg-amber-50';
        case 'In Progress':
            return 'text-primary border border-primary/30 bg-primary/5';
        case 'Done':
            return 'text-emerald-600 border border-emerald-300 bg-emerald-50';
        default:
            return 'text-muted-foreground border border-border';
    }
}

/* ---------- Letters ---------- */

// Merge tags available in letter templates (shown as a cheat sheet in the editor).
export const LETTER_FIELDS: { tag: string; label: string }[] = [
    { tag: 'incidentNumber', label: 'Incident #' },
    { tag: 'employeeName', label: 'Employee name' },
    { tag: 'entityName', label: 'Employer entity' },
    { tag: 'project', label: 'Project / Site' },
    { tag: 'supervisor', label: 'Supervisor' },
    { tag: 'date', label: 'Incident date' },
    { tag: 'time', label: 'Incident time' },
    { tag: 'witnesses', label: 'Witnesses' },
    { tag: 'cause', label: 'Cause of injury' },
    { tag: 'bodyPart', label: 'Body part' },
    { tag: 'nature', label: 'Nature of injury' },
    { tag: 'description', label: 'Description' },
    { tag: 'status', label: 'Status' },
    { tag: 'capacity', label: 'Capacity' },
    { tag: 'rtwCoordinator', label: 'RTW coordinator' },
    { tag: 'nextAppt', label: 'Next appointment' },
    { tag: 'today', label: "Today's date" },
];

const letterFieldValues = (i: Incident): Record<string, string> => ({
    incidentNumber: i.incidentNumber ?? '',
    employeeName: i.employeeName ?? '',
    entityName: i.entityName ?? '',
    project: i.project ?? '',
    supervisor: i.supervisor ?? '',
    date: i.date ? fmtDate(i.date) : '',
    time: i.time ?? '',
    witnesses: i.witnesses ?? '',
    cause: i.cause ?? '',
    bodyPart: i.bodyPart ?? '',
    nature: i.nature ?? '',
    description: i.description ?? '',
    status: i.status ?? '',
    capacity: i.capacity ?? '',
    rtwCoordinator: i.rtwCoordinator ?? '',
    nextAppt: i.nextAppt ? fmtDate(i.nextAppt) : '',
    today: fmtDate(todayISO()),
});

// Replace {{tag}} merge fields in a template body with incident values.
// Unknown tags are left untouched.
export function renderLetterBody(body: string, incident: Incident): string {
    const values = letterFieldValues(incident);
    return (body ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, tag) => values[tag] ?? match);
}

export const DEFAULT_LETTER_BODY = `Dear {{employeeName}},

RE: Workplace incident {{incidentNumber}} ({{date}})

We are writing regarding the incident reported on {{date}} at {{project}}.

Yours sincerely,
{{entityName}}`;

export function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* ---------- Register CSV export ---------- */

const csvCell = (v: unknown): string => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// CSV of the register's visible columns (matches IncidentRegister COLS).
export function buildIncidentsCsv(rows: Incident[]): string {
    const headers = ['Injury #', 'Worker Name', 'Employer Entity', 'Project', 'Supervisor', 'Date', 'Time', 'Status', 'Capacity', 'Next Appt', 'RTW Coordinator', 'Last Updated'];
    const lines = rows.map((r) =>
        [
            r.incidentNumber,
            r.employeeName,
            r.entityName,
            r.project,
            r.supervisor,
            r.date ? fmtDate(r.date) : '',
            r.time,
            r.status,
            r.capacity,
            r.nextAppt ? fmtDate(r.nextAppt) : '',
            r.rtwCoordinator,
            r.updatedAt ? fmtDate(r.updatedAt) : '',
        ].map(csvCell).join(','),
    );
    return [headers.map(csvCell).join(','), ...lines].join('\n');
}

// Trigger a browser download of CSV content.
export function downloadCsv(filename: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}
